import { z } from 'zod'
import type { CreateFeatureFlagRequest } from '@/schemas/feature-flag.schema'

const clipboardFlagSchema = z
  .object({
    name: z.string().trim(),
    key: z
      .string()
      .regex(
        /^[A-Z][A-Z0-9_]*$/,
        'The key must start with a letter and use uppercase letters, numbers, and underscores.',
      ),
    description: z.string().trim().optional(),
  })
  .strict()

export function isFeatureFlagClipboardText(text: string): boolean {
  return /^(?:\{|```(?:json)?\s*\{)/.test(text.trim())
}

export function parseFeatureFlagClipboard(
  text: string,
): CreateFeatureFlagRequest {
  if (text.length > 10000) {
    throw new Error(
      'The pasted flag is too large. Paste just its name, key, and description.',
    )
  }
  const source = text
    .trim()
    .replace(/^```(?:json)?\s*\n?([\s\S]*?)\n?```$/, '$1')
  let parsed: unknown
  try {
    parsed = JSON.parse(source)
  } catch {
    throw new Error(
      'Paste a JSON object with name, key, and an optional description.',
    )
  }
  const result = clipboardFlagSchema.safeParse(parsed)
  if (!result.success) {
    const issue = result.error.issues[0]
    throw new Error(
      issue?.code === 'unrecognized_keys'
        ? 'Paste only name, key, and description. Configure audiences after creating the flag.'
        : (issue?.message ?? 'Invalid flag format.'),
    )
  }
  return result.data
}
